/** @jsxImportSource @emotion/react */
import { cloneElement, memo, useCallback } from 'react';
import { css } from '@emotion/react';
import { Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAtom } from 'jotai';
import useNotifications from '../../../hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../../../../modules/login/v1/utility';
import useAddonPurchase from '../../../hooks/use-addon-purchase';
import { openPopup } from '../../../utils/popup';
import { MAIN_IN_SUPPORT_NUMBER } from '../../../constants';
import QSModal from '../modal';
import QSIconButton from '../../atoms/icon-button';
import ProgressiveImage from '../../atoms/progressive-image';
import { QSButton, QSTypography } from '../../atoms';
import Loader from '../loader';
import PlayCircle from '../../../icons/PlayCircle';
import { purchaseBlockerModalAtom } from './states';
import { PURCHASE_BLOCKER_MODAL_CONSTANTS } from './constants';

const PurchaseBlockerModal = memo(() => {
  const [blockerKey, setBlockerKey] = useAtom(purchaseBlockerModalAtom);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase();

  const blockerConfig = blockerKey ? PURCHASE_BLOCKER_MODAL_CONSTANTS[blockerKey] : undefined;
  const orgName = channelData?.name ?? 'DoubleTick';
  const contactUsLink =
    blockerConfig && channelData?.supportNumber
      ? `https://wa.me/${channelData.supportNumber}?text=${encodeURIComponent(blockerConfig.purchaseNowText)}`
      : undefined;

  const onClose = useCallback(() => {
    setBlockerKey(undefined);
  }, [setBlockerKey]);

  const onClickContactUs = useCallback(async () => {
    if (!blockerConfig) return;

    try {
      if (hideDTDetails && contactUsLink) {
        openPopup(contactUsLink, '_blank');
      } else if (!hideDTDetails && blockerConfig.addonKey) {
        await purchaseAddon(blockerConfig.addonKey);
      } else if (!hideDTDetails && blockerConfig.enterpriseContactMessage) {
        openPopup(
          `https://wa.me/${MAIN_IN_SUPPORT_NUMBER}?text=${encodeURIComponent(blockerConfig.enterpriseContactMessage)}`,
          '_blank'
        );
      }
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, blockerConfig, contactUsLink, hideDTDetails, onClose, purchaseAddon]);

  const onClickWatchDemo = useCallback(async () => {
    if (!blockerConfig?.demoVideoLink) return;
    try {
      openPopup(blockerConfig.demoVideoLink, '_blank');
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred.',
        type: 'error',
      });
    }
  }, [addNotification, blockerConfig, onClose]);

  if (!blockerConfig) return null;

  const {
    blockerImage,
    blockerImageHideDT,
    blockerIcon,
    blockerIconLabel,
    blockerIconWrapperStyles,
    blockerIconLabelStyles,
    blockerTitle,
    blockerTitleSubtext,
    blockerSubtitle,
    features,
    purchaseButtonText,
    contactUsButtonText,
    demoVideoLink,
  } = blockerConfig;

  const imageSrc = hideDTDetails && blockerImageHideDT ? blockerImageHideDT : blockerImage;
  const iconLabelText = typeof blockerIconLabel === 'function' ? blockerIconLabel(orgName) : blockerIconLabel;

  const showPurchaseFlow = !hideDTDetails && !!blockerConfig.addonKey;
  const primaryButtonText = showPurchaseFlow
    ? purchaseButtonText ?? 'Purchase Now'
    : contactUsButtonText ?? 'Contact Us';

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={!!blockerKey}
      onClose={onClose}
    >
      <Box
        css={css`
          display: flex;
          flex-direction: column;
          overflow: hidden;
          outline: none;
          position: relative;
          width: 100%;
          max-width: 730px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <Box
          css={css`
            padding: 16px;
            border-radius: 8px;
            background: white;
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: center;
            gap: 24px;
            align-self: stretch;
            flex-wrap: wrap;
          `}
        >
          <Box
            css={css`
              flex: 1;
              display: flex;
              justify-content: flex-end;
              align-items: center;
              align-self: stretch;
              background-color: #f5f0e6;
              border-radius: 4px;
              width: 320px;
              overflow: hidden;
            `}
          >
            <ProgressiveImage imgProps={{ width: '100%', height: '420px' }} src={imageSrc} />
          </Box>

          <Box
            css={css`
              flex: 1 0 0;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: space-between;
              height: 420px;
              max-width: 350px;
              align-self: stretch;
              padding: ${blockerKey === 'sso' ? '12px 0 12px 20px' : '0px'};
            `}
          >
            <Box
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <Box
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 8px;
                  flex: 1 0 0;
                `}
              >
                <Box
                  css={css`
                    display: inline-flex;
                    padding: 4px;
                    align-items: center;
                    gap: ${iconLabelText ? '8px' : '10px'};
                    border-radius: 4px;
                    background: rgba(0, 112, 188, 0.1);
                  `}
                  style={blockerIconWrapperStyles}
                >
                  {blockerIcon}
                  {iconLabelText ? (
                    <QSTypography
                      css={css`
                        color: #2d2d2d;
                        font-size: 12px;
                        font-weight: 500;
                      `}
                      style={blockerIconLabelStyles}
                    >
                      {iconLabelText}
                    </QSTypography>
                  ) : null}
                </Box>

                <Box
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                  `}
                >
                  <QSTypography
                    css={css`
                      color: #2d2d2d;
                      font-size: 20px;
                      font-weight: 600;
                      line-height: 140%;
                    `}
                  >
                    {blockerTitle}
                    {blockerTitleSubtext ? (
                      <span
                        css={css`
                          color: #7a7a7a;
                        `}
                      >
                        {' '}
                        {blockerTitleSubtext}
                      </span>
                    ) : null}
                  </QSTypography>

                  {blockerSubtitle ? (
                    <QSTypography
                      css={css`
                        font-weight: 400;
                        font-size: 14px;
                        line-height: 18px;
                        color: #7a7a7a;
                      `}
                    >
                      {blockerSubtitle}
                    </QSTypography>
                  ) : null}
                </Box>
              </Box>

              <Box
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 8px;
                `}
              >
                {features.map((feature) => {
                  const hasDescription = Boolean(feature.description);
                  const featureIcon = feature.icon
                    ? feature.skipIconDefaults
                      ? feature.icon
                      : cloneElement(feature.icon, {
                          color: '#3F906D',
                          width: '24px',
                          height: '24px',
                          ...(feature.iconProps ?? {}),
                        })
                    : null;

                  return (
                    <Box
                      key={feature.id}
                      css={css`
                        display: flex;
                        width: 100%;
                        align-items: ${hasDescription ? 'flex-start' : 'center'};
                        gap: ${hasDescription ? '12px' : '8px'};
                      `}
                    >
                      {featureIcon ? (
                        <Box
                          css={css`
                            display: flex;
                            align-items: center;
                            justify-content: center;
                          `}
                          style={feature.iconWrapperStyles}
                        >
                          {featureIcon}
                        </Box>
                      ) : null}
                      <Box
                        css={css`
                          display: flex;
                          flex-direction: column;
                          align-items: flex-start;
                          gap: 4px;
                          flex: 1 0 0;
                        `}
                      >
                        <QSTypography
                          css={css`
                            color: #2d2d2d;
                            font-size: ${hasDescription ? '14px' : '12px'};
                            font-weight: 500;
                          `}
                        >
                          {feature.title}
                        </QSTypography>
                        {hasDescription ? (
                          <QSTypography
                            css={css`
                              color: #7a7a7a;
                              font-size: 12px;
                              font-weight: 500;
                            `}
                          >
                            {feature.description}
                          </QSTypography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              css={css`
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
              `}
            >
              <QSButton
                css={css`
                  flex: ${demoVideoLink && hideDTDetails ? 'none' : '1 0 0'};
                  width: ${demoVideoLink && hideDTDetails ? '60%' : '100%'};
                  display: flex;
                  height: 40px;
                  padding: 8px 22px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background: #3c8461;
                  white-space: nowrap;
                  box-shadow: none !important;
                  &:hover {
                    box-shadow: none !important;
                  }
                `}
                onClick={onClickContactUs}
              >
                {showPurchaseFlow ? (
                  purchaseLoading ? (
                    <Loader size={16} loaderProps={{ sx: { color: 'white' } }} />
                  ) : (
                    primaryButtonText
                  )
                ) : (
                  primaryButtonText
                )}
              </QSButton>

              {!hideDTDetails && demoVideoLink && (
                <QSButton
                  variant="outlined"
                  css={css`
                    width: fit-content;
                    display: flex;
                    height: 40px;
                    padding: 12px 28px;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    border: 1px solid #e0e0e9;
                    background: #fff;
                    color: inherit;
                    outline: none;
                    box-shadow: none;
                    &:hover {
                      background: #f5f5f8;
                      outline: none;
                      box-shadow: none;
                    }
                  `}
                  onClick={onClickWatchDemo}
                >
                  <PlayCircle
                    size={20}
                    css={css`
                      flex-shrink: 0;
                    `}
                  />{' '}
                  Demo
                </QSButton>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </QSModal>
  );
});

export default PurchaseBlockerModal;
